import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Building2, 
  MessageSquare,
  AlertCircle
} from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter 
} from "@/components/ui/dialog";
import PasswordConfirmationDialog from "./PasswordConfirmationDialog";

interface ReactivationRequest {
  id: string;
  company_id: string;
  membership_id: string;
  requested_by: string;
  request_message: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  resolved_at: string | null;
  admin_response_message: string | null;
  // Joined data
  company_name: string;
  user_name: string;
  user_email: string;
  previous_role: string;
  deactivation_reason: string | null;
}

interface ReactivationRequestsManagementProps {
  companyId: string;
}

const ReactivationRequestsManagement = ({ companyId }: ReactivationRequestsManagementProps) => {
  const notify = useNotify();
  const [requests, setRequests] = useState<ReactivationRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<ReactivationRequest | null>(null);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null);

  const fetchRequests = async () => {
    try {
      console.log('Fetching reactivation requests for company:', companyId);
      
      // First fetch the basic reactivation requests
      const { data: requests, error: requestsError } = await supabase
        .from('reactivation_requests')
        .select('id, company_id, membership_id, requested_by, request_message, status, created_at, resolved_at, admin_response_message')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      console.log('Requests debug - Basic requests:', requests, 'Requests error:', requestsError);

      if (requestsError) throw requestsError;

      if (!requests || requests.length === 0) {
        setRequests([]);
        return;
      }

      // Get company name
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
        
      console.log('Company debug - Company data:', company, 'Company error:', companyError);

      // Get unique user IDs and membership IDs
      const userIds = [...new Set(requests.map(req => req.requested_by))];
      const membershipIds = [...new Set(requests.map(req => req.membership_id))];

      // Fetch profiles for all users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);
        
      console.log('Profiles debug - User profiles:', profiles, 'Profiles error:', profilesError);

      // Fetch company members data
      const { data: members, error: membersError } = await supabase
        .from('company_members')
        .select('id, role, deactivation_reason')
        .in('id', membershipIds);
        
      console.log('Members debug - Company members:', members, 'Members error:', membersError);

      const formattedRequests = requests.map(req => {
        const profile = profiles?.find(p => p.id === req.requested_by) || { first_name: '', last_name: '', email: '' };
        const member = members?.find(m => m.id === req.membership_id) || { role: '', deactivation_reason: null };
        
        return {
          id: req.id,
          company_id: req.company_id,
          membership_id: req.membership_id,
          requested_by: req.requested_by,
          request_message: req.request_message,
          status: req.status as 'pending' | 'approved' | 'rejected',
          created_at: req.created_at,
          resolved_at: req.resolved_at,
          admin_response_message: req.admin_response_message,
          company_name: company?.name || 'Unknown Company',
          user_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User',
          user_email: profile.email || 'No email',
          previous_role: member.role || 'Unknown',
          deactivation_reason: member.deactivation_reason || null,
        };
      });

      console.log('Final formatted requests:', formattedRequests);
      setRequests(formattedRequests);
    } catch (error) {
      console.error('Error fetching reactivation requests:', error);
      notify.error("Failed to load reactivation requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: string, message?: string) => {
    try {
      setProcessingId(requestId);
      
      const { data, error } = await supabase.functions.invoke('approve-reactivation', {
        body: { 
          request_id: requestId,
          admin_response_message: message 
        }
      });

      if (error) throw error;

      notify.success("The user has been reactivated and notified.");

      fetchRequests();
    } catch (error: any) {
      console.error('Error approving request:', error);
      notify.error(error.message || "Failed to approve request");
    } finally {
      setProcessingId(null);
      setResponseMessage('');
      setSelectedRequest(null);
    }
  };

  const initiateApproval = (request: ReactivationRequest) => {
    setSelectedRequest(request);
    setPendingAction('approve');
    setShowPasswordConfirmation(true);
  };

  const initiateRejection = (request: ReactivationRequest) => {
    setSelectedRequest(request);
    setPendingAction('reject');
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmed = () => {
    if (!selectedRequest || !pendingAction) return;
    
    if (pendingAction === 'approve') {
      handleApproveRequest(selectedRequest.id, responseMessage);
    } else if (pendingAction === 'reject') {
      handleRejectRequest(selectedRequest.id, responseMessage);
    }
    
    setPendingAction(null);
  };

  const handleRejectRequest = async (requestId: string, message?: string) => {
    try {
      setProcessingId(requestId);
      
      const { data, error } = await supabase.functions.invoke('reject-reactivation', {
        body: { 
          request_id: requestId,
          admin_response_message: message 
        }
      });

      if (error) throw error;

      notify.success("The user has been notified of the rejection.");

      fetchRequests();
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      notify.error(error.message || "Failed to reject request");
    } finally {
      setProcessingId(null);
      setResponseMessage('');
      setSelectedRequest(null);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [companyId]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 animate-spin" />
          <span>Loading reactivation requests...</span>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <CheckCircle className="h-8 w-8 text-muted-foreground" />
            <h3 className="font-medium">No Reactivation Requests</h3>
            <p className="text-sm text-muted-foreground">
              There are currently no reactivation requests for your company.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reactivation Requests</h2>
        <Badge variant="outline">
          {requests.filter(r => r.status === 'pending').length} Pending
        </Badge>
      </div>

      <div className="grid gap-4">
        {requests.map((request) => (
          <Card key={request.id} className={request.status === 'pending' ? 'border-l-4 border-l-yellow-400' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {request.user_name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Previous role: {request.previous_role}
                    </span>
                    <span>•</span>
                    <span>{request.user_email}</span>
                  </CardDescription>
                </div>
                {getStatusBadge(request.status)}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p>Requested: {format(new Date(request.created_at), 'dd MMMM yyyy HH:mm')}</p>
                {request.resolved_at && (
                  <p>Resolved: {format(new Date(request.resolved_at), 'dd MMMM yyyy HH:mm')}</p>
                )}
              </div>

              {request.deactivation_reason && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs font-medium text-muted-foreground">Original deactivation reason:</Label>
                  <p className="text-sm mt-1">{request.deactivation_reason}</p>
                </div>
              )}

              {request.request_message && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-xs font-medium text-blue-700">User's message:</Label>
                  <p className="text-sm mt-1 text-blue-900">{request.request_message}</p>
                </div>
              )}

              {request.admin_response_message && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs font-medium text-muted-foreground">Admin response:</Label>
                  <p className="text-sm mt-1">{request.admin_response_message}</p>
                </div>
              )}

              {request.status === 'pending' && (
                <div className="flex gap-2 pt-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm" 
                        onClick={() => setSelectedRequest(request)}
                        disabled={processingId === request.id}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Approve Reactivation Request</DialogTitle>
                        <DialogDescription>
                          This will reactivate {request.user_name} in your company with their previous role: {request.previous_role}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="response">Response Message (Optional)</Label>
                          <Textarea
                            id="response"
                            placeholder="Welcome back! Your access has been restored..."
                            value={responseMessage}
                            onChange={(e) => setResponseMessage(e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => {
                          setSelectedRequest(null);
                          setResponseMessage('');
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={() => initiateApproval(request)}>
                          Approve Request
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedRequest(request)}
                        disabled={processingId === request.id}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Reject Reactivation Request</DialogTitle>
                        <DialogDescription>
                          This will reject the reactivation request from {request.user_name}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="rejection-reason">Reason for Rejection (Optional)</Label>
                          <Textarea
                            id="rejection-reason"
                            placeholder="Please explain why the request was rejected..."
                            value={responseMessage}
                            onChange={(e) => setResponseMessage(e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => {
                          setSelectedRequest(null);
                          setResponseMessage('');
                        }}>
                          Cancel
                        </Button>
                        <Button 
                          variant="destructive" 
                          onClick={() => initiateRejection(request)}
                        >
                          Reject Request
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Password Confirmation Dialog */}
      <PasswordConfirmationDialog
        isOpen={showPasswordConfirmation}
        onClose={() => {
          setShowPasswordConfirmation(false);
          setPendingAction(null);
        }}
        onSuccess={handlePasswordConfirmed}
        title={pendingAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        description={`Please enter your password to confirm this ${pendingAction === 'approve' ? 'approval' : 'rejection'}.`}
        actionLabel={pendingAction === 'approve' ? 'Approve Request' : 'Reject Request'}
        variant={pendingAction === 'approve' ? 'default' : 'destructive'}
      />
    </div>
  );
};

export default ReactivationRequestsManagement;